import React, { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, UserRound, LayoutDashboard, CarFront } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";

export const NAV = [
  { to: "/", label: "Home" },
  { to: "/vehicles", label: "Cars" },
  { to: "/stays", label: "Stays" },
  { to: "/coming-soon", label: "Coming 2027" },
  { to: "/estates", label: "Estates" },
  { to: "/shipping", label: "Shipping" },
  { to: "/partner", label: "Partner" },
  { to: "/investors", label: "Investors" },
  { to: "/about", label: "About" },
  { to: "/support", label: "Contact" },
];

export const Logo = ({ light = false }: { light?: boolean }) => (
  <Link to="/" data-testid="brand-logo" className="group flex shrink-0 items-center gap-3 whitespace-nowrap">
    <img src="/logo-mark.png" alt="Trust Logistics LLC logo" className="h-10 w-10 shrink-0 rounded-full bg-[#1B6B46] ring-1 ring-white/15" />
    <span className="leading-tight">
      <span className={`block font-serif text-lg font-extrabold tracking-tight ${light ? "text-white" : "text-white"}`}>Trust Logistics</span>
      <span className="block font-mono text-[9px] uppercase tracking-[0.3em] text-mint">Delivering Trust.</span>
    </span>
  </Link>
);

const linkCls = ({ isActive }: { isActive: boolean }) =>
  `whitespace-nowrap rounded-full px-2.5 py-2 text-sm font-medium transition-colors ${isActive ? "text-mint" : "text-white/75 hover:text-white"}`;

export const Header = () => {
  const { user, isStaff } = useAuth();
  const { s } = useSettings();
  const [open, setOpen] = useState(false);
  return (
    <header className="sticky top-0 z-50 border-b border-white/10 bg-[#1A201D]/95 text-white backdrop-blur-xl">
      {s?.announcement && <div data-testid="announcement-bar" className="bg-navy py-2 text-center text-xs text-white/85">{s.announcement}</div>}
      <div className="container-x flex h-[72px] items-center justify-between gap-4">
        <Logo />
        <nav className="hidden items-center gap-0.5 xl:flex" data-testid="main-nav">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.to === "/"} className={linkCls} data-testid={`nav-${n.label.toLowerCase().replace(" ", "-")}`}>{n.label}</NavLink>
          ))}
        </nav>
        <div className="flex items-center gap-2">
          <Link to="/my-bookings" className="hidden h-10 items-center rounded-full border border-white/15 px-4 text-sm font-medium text-white/85 hover:bg-white/10 2xl:inline-flex whitespace-nowrap" data-testid="header-my-bookings">My Bookings</Link>
          <Link to="/vehicles" className="btn-gold hidden whitespace-nowrap px-5 py-2.5 sm:inline-flex" data-testid="header-book-car"><CarFront size={16} /> Book a Car</Link>
          {isStaff && (
            <Link to="/admin" className="hidden h-10 w-10 items-center justify-center rounded-full border border-white/15 sm:flex" data-testid="header-admin-link" title="Admin">
              <LayoutDashboard size={17} />
            </Link>
          )}
          <Link to={user ? "/account" : "/login"} data-testid="header-account-link" title={user ? user.name : "Sign in"} className="hidden h-10 w-10 items-center justify-center rounded-full border border-white/15 sm:flex">
            <UserRound size={16} />
          </Link>
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button className="flex h-10 w-10 items-center justify-center rounded-full border border-white/15 xl:hidden" data-testid="mobile-menu-button" aria-label="Menu">
                <Menu size={18} />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[86%] max-w-sm bg-[#F8FAF9]">
              <SheetTitle className="font-serif text-xl">Menu</SheetTitle>
              <div className="mt-6 flex flex-col gap-1" data-testid="mobile-nav">
                {[...NAV, { to: "/airport-transfers", label: "Airport Pickups" }, { to: "/tours", label: "Tours" }, { to: "/services", label: "Services" }, { to: "/my-bookings", label: "My Bookings" }, { to: user ? "/account" : "/login", label: user ? "My Account" : "Sign in" }, ...(isStaff ? [{ to: "/admin", label: "Admin Dashboard" }] : [])].map((n) => (
                  <NavLink key={n.to + n.label} to={n.to} onClick={() => setOpen(false)} className="rounded-xl px-4 py-3 text-base font-medium text-navy hover:bg-navy/5" data-testid={`mobile-nav-${n.label.toLowerCase().replace(/ /g, "-")}`}>
                    {n.label}
                  </NavLink>
                ))}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};
